import React, {
  useContext,
} from 'react';
import clsx from 'clsx';
import Table from 'core/components/VirtualTable';
import {
  UpdateDataCallback,
} from 'core/components/VirtualTable/types';
import {
  ServiceAllocation,
  VisitInfo,
} from 'pages/Dashboard/pages/Billing/types';
import {
  eobColumns,
  getEOBServiceAllocationRowId,
  eobReadonlyColumns,
  eobReviewColumns,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/columns';
import {
  WizardContext,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/wizardContext';
import {
  isServiceLineInactive,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/serviceLineStatus';

type Props = {
  data: Partial<VisitInfo>;
  update?: UpdateDataCallback<ServiceAllocation>;
  readonly?: boolean;
  isReview?: boolean;
};

const editableColumnIds = [
  'allowedAmount',
  'copay',
  'deductible',
  'coinsurance',
  'insurancePayment',
  'nonCovered',
  'insuranceAdjustments',
  'autoWriteOff',
] as const;

export default function VisitBreakdownView({
  data,
  update,
  readonly,
  isReview = false,
}: Props) {
  const { currentEOB } = useContext(WizardContext);
  const eobId = currentEOB?.eob?.eobId ?? 0;
  const allocation = data?.allocation ?? [];
  const readonlyColumns = isReview ? eobReviewColumns : eobReadonlyColumns;
  const editableColumnsSet = new Set(editableColumnIds);
  const groupedColumnIds = new Set(['insuranceAdjustments', 'autoWriteOff']);
  const lastRowIndex = Math.max(allocation.length - 1, 0);

  return (
    <div className="flex relative min-h-[40px] w-full h-auto overflow-hidden">
      <div className="flex flex-1 flex-col populate-breakdown-table w-full">
        <Table<ServiceAllocation>
          columns={readonly || isReview ? readonlyColumns : eobColumns}
          data={allocation}
          getRowId={getEOBServiceAllocationRowId}
          updateData={update}
          hasFooter={!isReview && allocation.length > 1}
          classes={{
            body: '!m-0 !p-0',
            scrollContainer: '!p-0 !overflow-y-auto breakdown-scroll-container',
            header: 'px-4 !items-end w-full',
            outerColumn: (column) => clsx('!items-end', {
              '!items-stretch !bg-emerald-50 !border-t !border-dashed !border-emerald-400':
                groupedColumnIds.has(column.id),
              '!border-l !rounded-tl-lg': column.id === 'insuranceAdjustments',
              '!border-r !rounded-tr-lg': column.id === 'autoWriteOff',
            }),
            column: '!text-left',
            cell: (row, cell) => clsx('!h-auto !py-0', {
              '!opacity-70':
                isServiceLineInactive(row.original)
                && cell.column.id !== 'actions'
                && cell.column.id !== 'serviceName',
              '!items-stretch': editableColumnsSet.has(cell.column.id),
              '!bg-emerald-50 !border-dashed !border-emerald-400 cursor-default':
                groupedColumnIds.has(cell.column.id),
              '!border-l': cell.column.id === 'insuranceAdjustments',
              '!border-r': cell.column.id === 'autoWriteOff',
              '!border-b !rounded-bl-lg':
                cell.column.id === 'insuranceAdjustments' && row.index === lastRowIndex,
              '!border-b !rounded-br-lg':
                cell.column.id === 'autoWriteOff' && row.index === lastRowIndex,
            }),
            row: (val) => clsx('!items-stretch', {
              '!bg-sky-50': val.original?.eobId === eobId && !isServiceLineInactive(val.original),
              '!bg-red-50': isServiceLineInactive(val.original),
            }),
            outerHeader: 'visit-breakdown-header !w-full',
            footer: 'visit-breakdown-footer !w-full',
            outerRow: '!w-full',
            scrollbarGutter: 'inherit',
          }}
        />
      </div>
    </div>
  );
}
